import type { SerializedStyles } from '@emotion/react'
import type { Theme } from '@/theme'
import { useThemeContext } from '@/contexts/Theme'
import { useMemo } from 'react'
import clone from 'clone-deep'
export { css } from '@emotion/react'

export type UseStylesType<
  StyleProps,
  StyleKey extends string,
  StyleType = SerializedStyles
> = (styleProps?: StyleProps) => Record<StyleKey, StyleType>

export interface StyleCreator<StyleProps, StyleType = SerializedStyles> {
  (theme: Theme, styleProps: StyleProps): StyleType
}

export function makeStyles<
  StyleProps extends Record<string, unknown> = Record<string, unknown>,
  StyleKey extends string = string,
  StyleType = SerializedStyles
>(
  styles: Record<StyleKey, StyleType | StyleCreator<StyleProps, StyleType>>,
): UseStylesType<StyleProps, StyleKey, StyleType> {
  const useStyles = (styleProps?: StyleProps) => {
    const theme = useThemeContext()

    const currStyles = useMemo<Record<StyleKey, StyleType>>(() => {
      const stylesClone = clone(styles)
      const processedStyles = {} as Record<StyleKey, StyleType>

      for (const [key, styleOrCreator] of Object.entries(stylesClone)) {
        Object.assign(processedStyles, {
          [key]:
            typeof styleOrCreator === 'function'
              ? styleOrCreator(theme, styleProps)
              : styleOrCreator,
        })
      }

      return processedStyles
    }, [styleProps, theme])

    return currStyles
  }

  return useStyles
}
